import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faIntercom } from "@fortawesome/free-brands-svg-icons";
import { faStore, faTruck, faWalking } from "@fortawesome/pro-solid-svg-icons";
import { z } from "zod";
import { toObjectMap } from "../../utilities/dictionary";
import type {
    Translatable} from "../translations/translation";
import {
    translatableZodSchema,
    translation,
} from "../translations/translation";

const deliveryMethodTypes = [
    "TABLE_SERVICE",
    "HOME_DELIVERY",
    "PICK_UP",
    "DIGITAL",
] as const;

const deliveryMethodStatus = [
    "ENABLED", //Igång
    "DISABLED", //Borttaget
    "PAUSED", //Syns men inte valbart
] as const;

type DeliveryMethodStatus = (typeof deliveryMethodStatus)[number];

type DeliveryMethodType = (typeof deliveryMethodTypes)[number];
const deliveryMethodType = toObjectMap<DeliveryMethodType>([
    ...deliveryMethodTypes,
]);
const deliveryMethodTypeZodSchema = z.enum(deliveryMethodTypes);

type DeliveryMethod = {
    type: DeliveryMethodType;
    title: Translatable;
    subTitle: Translatable;
    status: DeliveryMethodStatus;
};

const deliveryMethodZodSchema: z.ZodSchema<DeliveryMethod> = z.object({
    type: deliveryMethodTypeZodSchema,
    title: translatableZodSchema,
    subTitle: translatableZodSchema,
    status: z.enum(deliveryMethodStatus),
});

const pickUpDeliveryMethod = {
    title: translation({
        sv: "Upphämtning",
        en: "Pick up",
    }),
    subTitle: translation({
        sv: "Jag hämtar mina varor själv",
        en: "Pick up by yourself",
    }),
    type: deliveryMethodType.PICK_UP,
    status: "ENABLED" as DeliveryMethodStatus,
};
const tableServiceDeliveryMethod = {
    title: translation({
        sv: "Äter på stället",
        en: "Table service",
    }),
    subTitle: translation({
        sv: "Jag äter på stället",
        en: "Table service",
    }),
    type: deliveryMethodType.TABLE_SERVICE,
    status: "DISABLED" as DeliveryMethodStatus,
};
const _homeDeliveryMethod = {
    title: translation({
        sv: "Hemleverans",
        en: "Home delivery",
    }),
    subTitle: translation({
        sv: "Till den adress du väljer",
        en: "To your chosen address.",
    }),
    type: deliveryMethodType.HOME_DELIVERY,
    status: "ENABLED" as DeliveryMethodStatus,
};

const digitalMethod = {
    title: translation({
        sv: "Digital",
        en: "Digital",
    }),
    subTitle: translation({
        sv: "",
        en: "",
    }),
    type: deliveryMethodType.DIGITAL,
    status: "DISABLED" as DeliveryMethodStatus,
};

const homeDeliveryMethodPaused = {
    title: translation({
        sv: "Hemleverans",
        en: "Home delivery",
    }),
    subTitle: translation({
        sv: "Ej tillgängligt för närvarande",
        en: "Not available at the moment",
    }),
    type: deliveryMethodType.HOME_DELIVERY,
    status: "PAUSED" as DeliveryMethodStatus,
};

const deliveryMethodMap: { [key in DeliveryMethodType]: DeliveryMethod } = {
    PICK_UP: pickUpDeliveryMethod,
    TABLE_SERVICE: tableServiceDeliveryMethod,
    HOME_DELIVERY: homeDeliveryMethodPaused,
    DIGITAL: digitalMethod,
};

const deliveryMethodFaIconMap: {
    [key in DeliveryMethodType]: IconDefinition;
} = {
    PICK_UP: faWalking,
    TABLE_SERVICE: faStore,
    HOME_DELIVERY: faTruck,
    DIGITAL: faIntercom,
};

const deliveryMethods = Object.values(deliveryMethodMap);
const defaultDeliveryMethod = pickUpDeliveryMethod;
export {
    defaultDeliveryMethod,
    deliveryMethodFaIconMap,
    deliveryMethodMap,
    deliveryMethods,
    deliveryMethodType,
    deliveryMethodTypes,
    deliveryMethodTypeZodSchema,
    deliveryMethodZodSchema,
    pickUpDeliveryMethod,
};
export type { DeliveryMethod, DeliveryMethodStatus, DeliveryMethodType };
